import React from 'react';
import {
	Controller,
} from 'swiper';
import {
	Swiper,
	SwiperSlide,
} from 'swiper/react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import GraphQL from '~/types/graphql';

import AttachedHeading, {
	AttachedHeadingAlignment,
} from '~/components/patterns/structuredValues/AttachedHeading';
import AttachedEnterpriseRibbon from '~/components/patterns/pricingPlans/AttachedEnterpriseRibbon';
import BenefitNameWithIcon from './BenefitNameWithIcon';
import BenefitSectionName from './BenefitSectionName';
import Caption from '~/components/patterns/headings/Caption';
import List, {
	ListSize,
} from '~/components/patterns/lists/List';
import MobilePlansCaptionWrapper from '~/components/patterns/pricingPlans/MobilePlansCaptionWrapper';
import PlanBox from '~/components/patterns/pricingPlans/PlanBox';
import PlanDetails from '~/components/patterns/pricingPlans/PlanDetails';
import PlanFeatureCell from '~/components/patterns/pricingPlans/PlanFeatureCell';
import PlanName from '../../../names/PlanName';
import PlanPriceSummary from '../components/PlanPriceSummary';
import SignupPlanComparisonButton from '~/components/app/SignupPlanComparisonButton';
import UnderlinedRows, {
	UnderlinedRowsStyle,
} from '~/components/patterns/lists/UnderlinedRows';

import useAccountFeaturesAvailability from '~/hooks/useAccountFeaturesAvailability';
import useAccountId from '~/hooks/useAccountId';
import useConnectedSwipers from '~/hooks/useConnectedSwipers';

import {
	LIST_OF_ALL_SECTIONS,
	SECTION_ACCESS_CONTROL,
	SECTION_ADVANCED_ANALYSIS,
	SECTION_ADVANCED_FEATURES,
	SECTION_COLLABORATION,
	SECTION_CORE_FEATURES,
	SECTION_INTEGRATIONS,
	SECTION_MONITORING_SETTINGS,
	SECTION_SERVICES,
} from '~/model/benefits';

import {
	LIST_OF_ALL_PLANS,
	getNewPlanBenefits,
} from '~/model/universal';



const messages = defineMessages({
	mostPopular: {
		id: 'ui.planBenefits.flags.mostPopular',
	},
});

type Props = Record<string, any>;



const SLIDE_MAX_WIDTH = 350;
const SLIDE_WIDTH = '82%';



const MobilePlansComparison = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
	const {
		disabledPlans,
		focusedPlan,
		mostPopularPlan,
	} = props;

	const accountId = useAccountId();

	const accountFeaturesAvailability = useAccountFeaturesAvailability();

	const {
		getSwiperParams,
	} = useConnectedSwipers(
		LIST_OF_ALL_PLANS.v4.indexOf(focusedPlan),
		{
			centeredSlides: true,
			initialSlide: LIST_OF_ALL_PLANS.v4.indexOf(GraphQL.AccountPlan.Standard),
			modules: [Controller],
			slidesPerView: 'auto',
			spaceBetween: 10,
		},
		[
			'overview',
			SECTION_CORE_FEATURES,
			SECTION_ADVANCED_FEATURES,
			SECTION_ADVANCED_ANALYSIS,
			SECTION_INTEGRATIONS,
			SECTION_MONITORING_SETTINGS,
			SECTION_COLLABORATION,
			SECTION_SERVICES,
			SECTION_ACCESS_CONTROL,
		],
	);

	if (
		accountFeaturesAvailability === null
	) {
		return null;
	}

	const swiperSlideStyles = {
		maxWidth: SLIDE_MAX_WIDTH,
		width: SLIDE_WIDTH,
	};

	return (
		<List
			ref={ref}
			size={ListSize.XLarge}
		>
			<Swiper {...getSwiperParams('overview')}>
				{LIST_OF_ALL_PLANS.v4.toArray().map((planType, i) => {
					const isDisabled = disabledPlans[planType] !== undefined;

					return (
						<SwiperSlide
							key={'overview-plan-' + i}
							style={swiperSlideStyles}
						>
							<PlanBox
								details={(
									<PlanDetails
										ctaButton={(
											<SignupPlanComparisonButton plan={planType} />
										)}
										isDisabled={isDisabled}
										name={planType === GraphQL.AccountPlan.Enterprise ? (
											<AttachedEnterpriseRibbon>
												<PlanName plan={planType} />
											</AttachedEnterpriseRibbon>
										) : (
											<PlanName plan={planType} />
										)}
										priceOverview={accountId !== null && (
											<PlanPriceSummary
												accountId={accountId}
												disabledReason={disabledPlans[planType]}
												planType={planType}
											/>
										)}
									/>
								)}
								flag={planType === mostPopularPlan && (
									<FormattedMessage {...messages.mostPopular} />
								)}
								isDisabled={isDisabled}
								isEnterprise={planType === GraphQL.AccountPlan.Enterprise}
							/>
						</SwiperSlide>
					);
				})}
			</Swiper>

			<AttachedHeading
				heading={(
					<Caption>
						<MobilePlansCaptionWrapper
							maxWidth={SLIDE_MAX_WIDTH}
							width={SLIDE_WIDTH}
						>
							<BenefitSectionName
								section={SECTION_CORE_FEATURES}
							/>
						</MobilePlansCaptionWrapper>
					</Caption>
				)}
				headingAlignment={AttachedHeadingAlignment.Left}
			>
				<Swiper {...getSwiperParams(SECTION_CORE_FEATURES)}>
					{LIST_OF_ALL_PLANS.v4.toArray().map((planType) => {
						const isDisabled = disabledPlans[planType] !== undefined;

						return (
							<SwiperSlide
								key={planType}
								style={swiperSlideStyles}
							>
								<PlanBox
									benefits={(
										<UnderlinedRows style={planType === GraphQL.AccountPlan.Enterprise ? UnderlinedRowsStyle.Light : UnderlinedRowsStyle.Default}>
											{getNewPlanBenefits(accountFeaturesAvailability, 'coreFeatures').map((item, index) => (
												<PlanFeatureCell
													key={'core-features-benefit-' + index}
													label={(
														<BenefitNameWithIcon
															benefit={item.name}
															hint={item.hint}
															iconType={item.iconType}
															isEnterprise={planType === GraphQL.AccountPlan.Enterprise}
															section={SECTION_CORE_FEATURES}
														/>
													)}
													value={item.values[planType]}
												/>
											))}
										</UnderlinedRows>
									)}
									isDisabled={isDisabled}
									isEnterprise={planType === GraphQL.AccountPlan.Enterprise}
									title={planType === GraphQL.AccountPlan.Enterprise ? (
										<AttachedEnterpriseRibbon>
											<PlanName plan={planType} />
										</AttachedEnterpriseRibbon>
									) : (
										<PlanName plan={planType} />
									)}
								/>
							</SwiperSlide>
						);
					})}
				</Swiper>
			</AttachedHeading>

			{LIST_OF_ALL_SECTIONS.map((section) => (
				<AttachedHeading
					heading={(
						<Caption>
							<MobilePlansCaptionWrapper
								maxWidth={SLIDE_MAX_WIDTH}
								width={SLIDE_WIDTH}
							>
								<BenefitSectionName
									section={section}
								/>
							</MobilePlansCaptionWrapper>
						</Caption>
					)}
					headingAlignment={AttachedHeadingAlignment.Left}
					key={section}
				>
					<Swiper {...getSwiperParams(section)}>
						{LIST_OF_ALL_PLANS.v4.toArray().map((plan) => {
							const isDisabled = disabledPlans[plan] !== undefined;

							return (
								<SwiperSlide
									key={plan}
									style={swiperSlideStyles}
								>
									<PlanBox
										benefits={(
											<UnderlinedRows style={plan === GraphQL.AccountPlan.Enterprise ? UnderlinedRowsStyle.Light : UnderlinedRowsStyle.Default}>
												{getNewPlanBenefits(accountFeaturesAvailability, section).map((item) => (
													<PlanFeatureCell
														key={item.name}
														label={(
															<BenefitNameWithIcon
																benefit={item.name}
																hint={item.hint}
																iconType={item.iconType}
																isEnterprise={plan === GraphQL.AccountPlan.Enterprise}
																section={section}
															/>
														)}
														value={React.cloneElement(
															item.values[plan],
															{
																alignment: 'right',
															},
														)}
													/>
												))}
											</UnderlinedRows>
										)}
										isDisabled={isDisabled}
										isEnterprise={plan === GraphQL.AccountPlan.Enterprise}
										title={plan === GraphQL.AccountPlan.Enterprise ? (
											<AttachedEnterpriseRibbon>
												<PlanName plan={plan} />
											</AttachedEnterpriseRibbon>
										) : (
											<PlanName plan={plan} />
										)}
									/>
								</SwiperSlide>
							);
						})}
					</Swiper>
				</AttachedHeading>
			))}
		</List>
	);
});



export default MobilePlansComparison;
